// Группы левого меню — «один пункт, вкладки внутри».
//
// Зачем: до 2026-07 в меню было 134 пункта, у рядового сотрудника — 20 в одном
// «Личном кабинете». Решения рабочей сессии зафиксированы в
// docs/specs/navigation-redesign-2026-07.md: близкие по смыслу разделы
// сворачиваются в один пункт с полосой вкладок внутри.
//
// Механика намеренно НЕ трогает URL: каждая вкладка — существующий маршрут,
// группа лишь оборачивает их безпутевым layout-роутом (SectionShell) и рисует
// полосу вкладок. Поэтому старые ссылки, закладки и deep-link'и из писем
// продолжают работать, а module_seed/RBAC остаются прежними — видимость
// вкладки считается по её собственному ключу модуля.
import {
    Activity, AlarmClock, BarChart3, BookOpen, Brain, Briefcase, Building2, Calendar,
    CalendarClock, CalendarDays, ClipboardCheck, ClipboardList, Clock, Compass, Crosshair,
    FileSignature, FileText, Gift, GitBranch, GraduationCap, Grid3x3, Handshake, Heart,
    Inbox, Key, Laptop, LayoutDashboard, LifeBuoy, MapPin, MessageCircle, Network, Package,
    PenLine, PieChart, Presentation, Search, ShieldCheck, Sparkles, Star, Target, Telescope,
    TrendingUp, Upload, UserCheck, UserMinus, UserPlus, Users, UsersRound, Wallet, Zap,
} from 'lucide-react'

// key → { label, icon, tabs: [{ to, label, icon, moduleKey? }] }
// Первая ВИДИМАЯ вкладка становится целью пункта меню (см. resolveGroupTarget).
export const NAV_GROUPS = {
    // ── Личный кабинет ──
    learning: {
        label: 'Обучение',
        icon: GraduationCap,
        tabs: [
            { to: 'me/learning', label: 'Мои курсы', icon: BookOpen },
            { to: 'growth-me', label: 'Развитие', icon: TrendingUp },
            { to: 'dialogue-trainer', label: 'Тренажёр', icon: MessageCircle },
            { to: 'me/skill-profile', label: 'Навыки', icon: Target },
            // «Мои материалы» (growth-kb) убраны из кабинета линейного
            // онлайн-менеджера 2026-08-02 (Наталья). Под этим названием лежит
            // редактор статей в Базу знаний с review-flow, а не склад наград —
            // писать статьи в базу знаний не работа онлайн-менеджера.
            // Модуль сохранён ролям из module_seed('growth-kb').
            { to: 'growth-kb', label: 'Мои материалы', icon: BookOpen, moduleKey: 'growth-kb' },
        ],
    },
    'my-services': {
        label: 'Мои сервисы',
        icon: LifeBuoy,
        tabs: [
            { to: 'inbox', label: 'Мои задачи', icon: Inbox },
            { to: 'support', label: 'Поддержка', icon: LifeBuoy },
            { to: 'benefits', label: 'Льготы и бонусы', icon: Heart },
            { to: 'internal-jobs', label: 'Карьера внутри', icon: Briefcase },
            { to: 'referrals', label: 'Рекомендую друга', icon: UserPlus },
            { to: 'rewards', label: 'Магазин наград', icon: Gift },
        ],
    },
    tools: {
        label: 'Инструменты',
        icon: Sparkles,
        tabs: [
            { to: 'ai-studio', label: 'AI Studio', icon: Sparkles },
            { to: 'teambuilder', label: 'Team Builder', icon: UserCheck },
            { to: 'workflows', label: 'Workflow Studio', icon: Activity },
        ],
    },

    // ── HR ──
    people: {
        label: 'Сотрудники',
        icon: Users,
        tabs: [
            { to: 'employees', label: 'Список', icon: Users },
            { to: 'orgchart', label: 'Оргструктура', icon: Network },
        ],
    },
    absences: {
        label: 'Отсутствия',
        icon: CalendarDays,
        tabs: [
            { to: 'timeoff', label: 'Отпуска', icon: CalendarDays },
            { to: 'calendar', label: 'Календарь команды', icon: Calendar },
        ],
    },
    hiring: {
        label: 'Наём и адаптация',
        icon: UserPlus,
        tabs: [
            { to: 'resume-scraper', label: 'Суперкнопка', icon: Zap },
            { to: 'hr-lifecycle', label: 'Жизненный цикл', icon: ClipboardCheck },
            { to: 'onboarding-paths', label: 'Треки адаптации', icon: GraduationCap },
            { to: 'onboarding-dashboard', label: 'Эффективность онбординга', icon: UserCheck },
            { to: 'offboardings', label: 'Оффбординги', icon: UserMinus },
        ],
    },
    'growth-review': {
        label: 'Развитие и оценка',
        icon: TrendingUp,
        tabs: [
            { to: 'growth-team', label: 'Развитие команды', icon: TrendingUp },
            { to: 'reviews', label: 'Ревью', icon: ClipboardCheck },
            { to: 'okr', label: 'Цели (OKR)', icon: Target },
            { to: 'kudos', label: 'Благодарности', icon: Heart },
            { to: 'growth-analytics', label: 'Аналитика развития', icon: Activity },
            { to: 'people-analytics', label: 'People Analytics', icon: BarChart3 },
        ],
    },

    // ── Вне HR ──
    equipment: {
        label: 'Оборудование и ключи',
        icon: Laptop,
        tabs: [
            { to: 'assets', label: 'Оборудование', icon: Laptop },
            { to: 'keys', label: 'Ключи', icon: Key },
        ],
    },
    marketing: {
        label: 'Маркетинг',
        icon: BarChart3,
        tabs: [
            { to: 'marketing', label: 'Обзор', icon: BarChart3 },
            { to: 'contractors', label: 'Подрядчики', icon: Handshake },
            { to: 'ad-budget', label: 'Пополнения рекламы', icon: Wallet },
        ],
    },

    // ── Рекрутинг ──
    candidates: {
        label: 'Кандидаты',
        icon: Users,
        tabs: [
            { to: 'candidates', label: 'Все кандидаты', icon: Users },
            { to: 'pipeline', label: 'Воронка', icon: Activity },
            { to: 'shortlist', label: 'Шортлист', icon: Star },
            { to: 'talent-pool', label: 'Кадровый резерв', icon: UserCheck },
        ],
    },
    vacancies: {
        label: 'Вакансии и заявки',
        icon: Briefcase,
        tabs: [
            { to: 'vacancies', label: 'Вакансии', icon: Briefcase },
            { to: 'requisitions', label: 'Заявки на подбор', icon: FileText },
            { to: 'job-profiles', label: 'Профили должностей', icon: Target },
        ],
    },
    offers: {
        label: 'Офферы и подписание',
        icon: FileText,
        tabs: [
            { to: 'offers', label: 'Офферы', icon: FileText },
            { to: 'signing', label: 'Документы на подпись', icon: PenLine },
        ],
    },
    hh: {
        label: 'HH.ru',
        icon: Search,
        tabs: [
            { to: 'hh-responses', label: 'Отклики', icon: Inbox },
            { to: 'hh-search', label: 'Поиск резюме', icon: Search },
            { to: 'hh-messenger', label: 'Переписка', icon: MessageCircle },
            { to: 'hh-vacancy-stats', label: 'Статистика вакансий', icon: BarChart3 },
        ],
    },
    'hiring-analytics': {
        label: 'Аналитика найма',
        icon: BarChart3,
        tabs: [
            { to: 'analytics', label: 'Аналитика рынка', icon: BarChart3 },
            { to: 'process', label: 'Бизнес-процесс', icon: Clock },
        ],
    },

    // ── Руководитель подразделения ──
    'dept-team': {
        label: 'Моя команда',
        icon: Users,
        tabs: [
            { to: 'myteam', label: 'Люди', icon: Users },
            { to: 'dept-dashboard', label: 'Дашборд подразделения', icon: Building2 },
        ],
    },
    'team-learning': {
        label: 'Обучение команды',
        icon: GraduationCap,
        tabs: [
            { to: 'team/learning', label: 'Прогресс', icon: GraduationCap },
            { to: 'team-test-results', label: 'Результаты тестов', icon: ClipboardList },
            { to: 'team-insights/trainer-vs-real', label: 'Калибровка тренажёра', icon: Crosshair },
            { to: 'team-insights/readiness', label: 'Готовность команды', icon: Grid3x3 },
        ],
    },
    'team-time': {
        label: 'Табель и графики',
        icon: Clock,
        tabs: [
            { to: 'team-timesheet', label: 'Табель команды', icon: Clock },
            { to: 'work-schedules', label: 'Графики работ', icon: CalendarClock },
        ],
    },
    'team-money': {
        label: 'Выплаты команде',
        icon: Wallet,
        tabs: [
            { to: 'team-payments', label: 'Ведомость', icon: Wallet },
            { to: 'payment-tasks', label: 'Задачи выплат', icon: Wallet },
        ],
    },

    // ── Онлайн-команда ──
    'om-team': {
        label: 'Команда менеджеров',
        icon: Users,
        tabs: [
            { to: 'manager-team', label: 'Менеджеры', icon: Users },
            { to: 'kpi-targets', label: 'KPI-планы', icon: Target },
            { to: 'my-colleagues', label: 'Мои коллеги', icon: Users },
        ],
    },
    // 2026-07-28: группа «Обмен и бот» расформирована. Senate Ops Dashboard —
    // экран собственников и CEO (живёт в разделе «Стратегия компании»), PS2 Bot
    // остаётся у онлайн-команды отдельным пунктом. Соседство двух экранов во
    // вкладках создавало у менеджеров впечатление одного модуля.

    // ── Стратегия ──
    'biz-metrics': {
        label: 'Показатели бизнеса',
        icon: TrendingUp,
        tabs: [
            { to: 'exchange', label: 'Exchange Dashboard', icon: TrendingUp },
            { to: 'okm', label: 'Ключевые метрики (ОКМ)', icon: Target },
            { to: 'headcount-planning', label: 'Планирование штата', icon: PieChart },
        ],
    },
    orgdesign: {
        label: 'Оргдизайн',
        icon: Network,
        tabs: [
            { to: 'operating-model', label: 'Операционная модель', icon: Compass },
            { to: 'spans-of-control', label: 'Норма управляемости', icon: Network },
            { to: 'skills-footprint', label: 'Карта компетенций', icon: Brain },
            { to: 'behaviors', label: 'Поведенческие паттерны', icon: Heart },
            { to: 'quarterly-governance', label: 'Квартальный обзор OKR', icon: Target },
        ],
    },

    // ── Партнёрка ──
    'partner-desk': {
        label: 'Рабочий стол',
        icon: LayoutDashboard,
        tabs: [
            { to: 'partner-desk', label: 'Мой стол', icon: LayoutDashboard },
            { to: 'partner-plan-gap', label: 'План месяца', icon: Target },
            { to: 'partner-team-compare', label: 'Я и команда', icon: UsersRound },
            { to: 'partner-team', label: 'Команда', icon: Users },
        ],
    },
    'partner-base': {
        label: 'Партнёры',
        icon: Handshake,
        tabs: [
            { to: 'partner-crm', label: 'База (CRM)', icon: Handshake },
            { to: 'partner-network-tree', label: 'Дерево сети', icon: GitBranch, moduleKey: 'partner-crm' },
            { to: 'partner-crm/geo-leads', label: 'Гео-лиды', icon: MapPin },
        ],
    },
    'partner-insight': {
        label: 'Аналитика партнёрки',
        icon: TrendingUp,
        tabs: [
            { to: 'partner-analytics-suite', label: 'Аналитика', icon: TrendingUp },
            { to: 'partner-exec', label: 'Стратегия', icon: Telescope },
            { to: 'okk', label: 'Контроль качества (ОКК)', icon: ShieldCheck },
        ],
    },
    'partner-refs': {
        label: 'Справочники партнёрки',
        icon: Package,
        tabs: [
            { to: 'partner-products', label: 'Продукты', icon: Package },
            { to: 'partner-plans', label: 'Планы продаж', icon: Target },
            // Норма дел на неделю задаётся здесь. Раньше экран существовал, но в
            // меню его не было — только по прямой ссылке, поэтому норму никто не
            // назначал и рабочий стол честно писал «план на неделю не задан».
            { to: 'partner-touch-plan', label: 'План дел на неделю', icon: CalendarDays },
            { to: 'partner-sla-admin', label: 'SLA-правила', icon: Clock },
            { to: 'partner-segments-admin', label: 'Критерии сегментов', icon: Target },
            { to: 'partner-import', label: 'Импорт CSV', icon: Upload },
        ],
    },
    'partner-docs': {
        label: 'Документы партнёрки',
        icon: FileSignature,
        tabs: [
            { to: 'partner-docs-marketing', label: 'Маркетинговые', icon: Presentation },
            { to: 'partner-docs-contracts', label: 'Договоры', icon: FileSignature },
        ],
    },
}

// Плоский список всех вкладок всех групп — для реестра навигации и для
// определения «в какой группе находится текущий маршрут».
export const ALL_GROUP_TABS = Object.entries(NAV_GROUPS).flatMap(
    ([groupKey, group]) => group.tabs.map(tab => ({ ...tab, groupKey, groupLabel: group.label })),
)

const ROUTE_TO_GROUP = new Map(ALL_GROUP_TABS.map(tab => [tab.to, tab.groupKey]))

/**
 * Ключ маршрута из pathname — зеркалит логику Layout.jsx (трекинг «Недавних»):
 * снимает префикс `/erp`, берёт максимум два сегмента и не считает вторым
 * сегментом идентификатор (число или uuid-подобное).
 */
export function routeKeyFromPathname(pathname) {
    const parts = String(pathname || '').split('/').filter(Boolean)
    const segs = parts[0] === 'erp' ? parts.slice(1) : parts
    const seg1 = segs[0] || ''
    const seg2 = segs[1] || ''
    const isId = /^\d+$|^[0-9a-f-]{8,}$/i.test(seg2)
    return seg1 && seg2 && !isId ? `${seg1}/${seg2}` : seg1
}

/** Группа, которой принадлежит текущий маршрут (или null). */
export function groupKeyForPathname(pathname) {
    const key = routeKeyFromPathname(pathname)
    if (ROUTE_TO_GROUP.has(key)) return ROUTE_TO_GROUP.get(key)
    // Под-маршрут вкладки: /employees/123/personnel-file → employees
    const seg1 = key.split('/')[0]
    return ROUTE_TO_GROUP.get(seg1) || null
}

/**
 * Цель пункта меню для группы: первая вкладка, доступная пользователю.
 * `isVisible(moduleKey)` — предикат видимости модуля (ModuleContext).
 * Возвращает null, если ни одна вкладка не доступна — тогда пункт не рисуется.
 */
export function resolveGroupTarget(groupKey, isVisible) {
    const group = NAV_GROUPS[groupKey]
    if (!group) return null
    const visible = group.tabs.filter(tab => isVisible(tab.moduleKey || tab.to))
    if (visible.length === 0) return null
    return {
        groupKey,
        label: group.label,
        icon: group.icon,
        to: visible[0].to,
        tabs: visible,
    }
}
